import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { PageShell, PageHero } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowRight } from "lucide-react";

const inr = (n) => new Intl.NumberFormat("en-IN").format(n);
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");

const SlotBox = ({ label, className }) => (
  <div className={`flex items-center justify-center rounded-lg border border-dashed border-gmoaa-border-strong bg-gmoaa-gold-light text-xs font-medium text-gmoaa-text-muted ${className}`}>
    {label}
  </div>
);

const Advertise = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.adRateCard().then((d) => setSlots(d.slots || [])).catch(() => setSlots([]));
    api.gaplSponsors().then(setSponsors).catch(() => setSponsors([]));
  }, []);

  const priceOf = (s) => (s.amount > 0 ? `₹ ${inr(s.amount)} ${lang === "gu" ? (s.period_gu || "") : (s.period_en || "")}` : t("onRequest"));

  return (
    <PageShell>
      <PageHero title={t("advertiseTitle")} subtitle={t("advertiseIntro")} />
      <div className="gmoaa-container py-12">
        {/* Slot diagram */}
        <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">{t("adSlots")}</h2>
        <div className="mx-auto max-w-md space-y-3 rounded-2xl border border-gmoaa-border-subtle bg-white p-5">
          <SlotBox label="Top Banner" className="h-12" />
          <div className="flex gap-3">
            <div className="flex-1 rounded-lg bg-gmoaa-green-light p-4 text-center text-xs text-gmoaa-green">Page content</div>
            <SlotBox label="Sidebar" className="h-28 w-28" />
          </div>
          <SlotBox label="Mid Banner" className="h-12" />
        </div>

        {/* Rate card */}
        <h2 className="mb-5 mt-12 font-serif text-2xl font-semibold text-gmoaa-green">{t("rateCard")}</h2>
        <div className="overflow-hidden rounded-xl border border-gmoaa-border-subtle bg-white" data-testid="advertise-rate-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-gmoaa-green-light">
                <TableHead>{t("slotName")}</TableHead>
                <TableHead>{t("size")}</TableHead>
                <TableHead>{t("rate")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slots.map((r) => (
                <TableRow key={r.id} data-testid={`rate-row-${r.id}`}>
                  <TableCell className="font-medium text-gmoaa-text-dark">{lang === "gu" ? r.slot_gu : r.slot_en}</TableCell>
                  <TableCell className="text-gmoaa-text-muted">{lang === "gu" ? r.placement_gu : r.placement_en}</TableCell>
                  <TableCell className="font-semibold text-gmoaa-saffron-hover" data-testid={`rate-price-${r.id}`}>{priceOf(r)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* GAPL match-centre sponsors */}
        {sponsors.length > 0 && (
          <>
            <h2 className="mb-5 mt-12 font-serif text-2xl font-semibold text-gmoaa-green">{t("matchSponsors")}</h2>
            <div className="flex flex-wrap items-center gap-4" data-testid="advertise-gapl-sponsors">
              {sponsors.map((s) => {
                const inner = (
                  <>
                    {s.logo_url ? <img src={mediaUrl(s.logo_url)} alt={s.name_en} className="max-h-10 max-w-full object-contain" /> : null}
                    <span className="text-xs font-medium text-gmoaa-text-muted">{lang === "gu" ? (s.name_gu || s.name_en) : s.name_en}</span>
                  </>
                );
                const cls = "flex h-20 w-44 flex-col items-center justify-center gap-1.5 rounded-lg border border-gmoaa-border-subtle bg-white px-3 py-2 text-center";
                return s.link ? (
                  <a key={s.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/gapl-sponsors/${s.id}/click`} target="_blank" rel="noopener noreferrer"
                    className={`${cls} transition-shadow hover:shadow-md`} data-testid={`advertise-sponsor-${s.id}`}>{inner}</a>
                ) : (
                  <div key={s.id} className={cls}>{inner}</div>
                );
              })}
            </div>
          </>
        )}

        <Button onClick={() => navigate("/contact")} className="mt-8 bg-gmoaa-green text-white hover:bg-gmoaa-green-hover" data-testid="advertise-enquire-btn">
          {t("enquire")} <ArrowRight size={16} className="ml-1.5" />
        </Button>
      </div>
    </PageShell>
  );
};

export default Advertise;
